import { useEffect, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore } from '@src/stores';

interface IUseFloatingButtonProps {
  toggle: (position?: { x: number; y: number }) => void;
  position?: { x: number; y: number } | null;
  onPositionChange?: (position: { x: number; y: number } | null) => void;
}

const useFloatingButton = ({ toggle, position, onPositionChange }: IUseFloatingButtonProps) => {
  const [hasDragged, setHasDragged] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  const { lastSelectedToolIcon } = useMagicPenStore(
    useShallow((state) => ({
      lastSelectedToolIcon: state.lastSelectedToolIcon,
    }))
  );

  const handleMouseDown = () => {
    setIsMouseDown(true);
    setShowTooltip(false);
  };

  useEffect(() => {
    const handleUp = () => {
      setIsMouseDown(false);
      setShowTooltip(true);
    };
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchend', handleUp);
    return () => {
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };
  }, []);

  const handleMouseLeave = () => {
    if (!isMouseDown) setShowTooltip(true);
  };

  const handleDragStart = () => {
    setHasDragged(false);
    setShowTooltip(false);
  };

  const handleDrag = (event: any, data: any) => {
    if (Math.abs(data.deltaX) > 5 || Math.abs(data.deltaY) > 5) {
      setHasDragged(true);
    }
  };

  const handleDragEnd = (event: any, data: any) => {
    const newPosition = { x: data.x, y: data.y };
    onPositionChange?.(newPosition);

    // Reset drag states with a very short delay to prevent click after drag
    setTimeout(() => {
      setHasDragged(false);
    }, 50);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (hasDragged) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const currentPosition = position || { x: 0, y: 0 };
    toggle({ x: currentPosition.x, y: 0 });
  };

  return {
    // State
    showTooltip,
    lastSelectedToolIcon,

    // Refs
    nodeRef,
    buttonRef,

    // Handlers
    handleDrag,
    handleClick,
    handleDragEnd,
    handleDragStart,
    handleMouseDown,
    handleMouseLeave,

    // Props
    position,
  };
};

export default useFloatingButton;
